import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { MapPin, Mail, Phone, ArrowUpRight, AlertCircle, CheckCircle2, X } from "lucide-react"
import { SiteNavbar } from "../../Navbar/SiteNavbar"
import { FooterSection } from "../../Footer/FooterSection"
import "./ContactPage.css"

const contactRows = [
  {
    icon: MapPin,
    label: "Address",
    value: "Al Sulay, Riyadh, Saudi Arabia",
    href: "https://maps.google.com/?q=Al+Sulay,+Riyadh,+Saudi+Arabia",
  },
  {
    icon: Mail,
    label: "Email",
    value: "info@roadsreflectors.com",
    href: "mailto:info@roadsreflectors.com",
  },
  {
    icon: Phone,
    label: "Administrator",
    value: "0504 465 224",
    href: "tel:+966504465224",
  },
  {
    icon: Phone,
    label: "Financial Administrator",
    value: "0500 455 042",
    href: "tel:+966500455042",
  },
]

export function ContactPage() {
  const { t } = useTranslation()
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" })
  const [website, setWebsite] = useState("")
  const [submissionResult, setSubmissionResult] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const dialogCloseRef = useRef(null)

  useEffect(() => {
    if (!submissionResult) return

    dialogCloseRef.current?.focus()
    const onKeyDown = (event) => {
      if (event.key === "Escape") setSubmissionResult(null)
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [submissionResult])

  function handleChange(e) {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      const response = await fetch("/api/contact.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, website }),
      })
      const result = await response.json()

      if (!response.ok || result.sent !== true) {
        throw new Error(result.error || "Contact form submission failed")
      }

      setForm({ name: "", email: "", phone: "", subject: "", message: "" })
      setWebsite("")
      setSubmissionResult("success")
    } catch (error) {
      console.error("Contact form submission failed:", error)
      setSubmissionResult("error")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <SiteNavbar />
      <main className="contact-page">
        {/* Hero */}
        <section className="contact-hero">
          <div className="container contact-hero__inner">
            <span className="contact-eyebrow contact-eyebrow--light">{t("Contact Us")}</span>
            <h1 className="contact-hero__title">{t("Let's talk road safety")}</h1>
            <p className="contact-hero__tagline">
              {t("Questions about a product, a quote, or an ongoing project? Send us your details and our team will get back to you.")}
            </p>
          </div>
        </section>

        {/* Form + info */}
        <section className="contact-section">
          <div className="container contact-grid">
            {/* Form card */}
            <div className="contact-form-card">
              <span className="contact-eyebrow">{t("Send a Message")}</span>
              <h2 className="contact-form-card__title">{t("Fill in your details")}</h2>

              <form className="contact-form" onSubmit={handleSubmit}>
                  <input
                    type="text"
                    name="website"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="contact-form__honeypot"
                  />
                  <div className="contact-field-row">
                    <div className="contact-field">
                      <label htmlFor="name">{t("Full name")}</label>
                      <input
                        id="name" name="name" type="text" required
                        value={form.name} onChange={handleChange}
                        placeholder={t("Your name")}
                      />
                    </div>
                    <div className="contact-field">
                      <label htmlFor="phone">{t("Phone")}</label>
                      <input
                        id="phone" name="phone" type="tel"
                        value={form.phone} onChange={handleChange}
                        placeholder="05xx xxx xxx"
                      />
                    </div>
                  </div>

                  <div className="contact-field-row">
                    <div className="contact-field">
                      <label htmlFor="email">{t("Email")}</label>
                      <input
                        id="email" name="email" type="email" required
                        value={form.email} onChange={handleChange}
                        placeholder="you@example.com"
                      />
                    </div>
                    <div className="contact-field">
                      <label htmlFor="subject">{t("Subject")}</label>
                      <input
                        id="subject" name="subject" type="text"
                        value={form.subject} onChange={handleChange}
                        placeholder={t("Quote, product, project…")}
                      />
                    </div>
                  </div>

                  <div className="contact-field">
                    <label htmlFor="message">{t("Message")}</label>
                    <textarea
                      id="message" name="message" rows={5} required
                      value={form.message} onChange={handleChange}
                      placeholder={t("Tell us how we can help")}
                    />
                  </div>

                  <button type="submit" className="btn-pill btn-pill--brand contact-form__submit" disabled={isSubmitting}>
                    {isSubmitting ? t("Sending...") : t("Send Message")}
                    <span className="btn-pill__arrow"><ArrowUpRight size={14} /></span>
                  </button>
              </form>
            </div>

            {/* Info column */}
            <aside className="contact-info">
              <div className="contact-info__panel">
                <span className="contact-eyebrow contact-eyebrow--light">{t("Reach Us")}</span>
                <h2 className="contact-info__title">{t("Contact details")}</h2>
                <div className="contact-rows">
                  {contactRows.map((row) => {
                    const Icon = row.icon
                    return (
                      <a key={row.label} href={row.href} className="contact-row">
                        <span className="contact-row__icon"><Icon size={18} /></span>
                        <span className="contact-row__text">
                          <span className="contact-row__label">{t(row.label)}</span>
                          <span
                            className="contact-row__value"
                            dir={row.href.startsWith("tel:") ? "ltr" : undefined}
                          >
                            {t(row.value)}
                          </span>
                        </span>
                        <span className="contact-row__go"><ArrowUpRight size={16} /></span>
                      </a>
                    )
                  })}
                </div>
              </div>
            </aside>
          </div>
        </section>

        {/* Map */}
        <section className="contact-section contact-section--tight">
          <div className="container">
            <div className="contact-map">
              <iframe
                title={t("Road's Reflectors location")}
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3625.27473103935!2d46.8416028!3d24.6830809!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e2fa7ac7d56ffed%3A0xe7c1f7fdc4ff254e!2z2YXYpNiz2LPYqSDYudmI2KfZg9izINin2YTYt9ix2YIg2KfZhNiq2KzYp9ix2YrYqQ!5e0!3m2!1sen!2sjo!4v1786210410221!5m2!1sen!2sjo"
                allowFullScreen
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>
          </div>
        </section>
      </main>
      <FooterSection />

      {submissionResult && (
        <div
          className="contact-result-backdrop"
          onClick={() => setSubmissionResult(null)}
          role="presentation"
        >
          <section
            className={`contact-result contact-result--${submissionResult}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="contact-result-title"
            aria-describedby="contact-result-message"
            onClick={(event) => event.stopPropagation()}
            dir={document.documentElement.dir}
          >
            <button
              ref={dialogCloseRef}
              type="button"
              className="contact-result__close"
              aria-label={t("Close")}
              onClick={() => setSubmissionResult(null)}
            >
              <X size={20} />
            </button>
            <span className="contact-result__icon" aria-hidden="true">
              {submissionResult === "success" ? <CheckCircle2 size={30} /> : <AlertCircle size={30} />}
            </span>
            <h2 id="contact-result-title">
              {submissionResult === "success" ? t("Thank you!") : t("Message not sent")}
            </h2>
            <p id="contact-result-message">
              {submissionResult === "success"
                ? t("Your message has been received. We'll be in touch shortly.")
                : t("Your message could not be sent. Please try again or email us directly.")}
            </p>
            <button
              type="button"
              className="btn-pill btn-pill--brand contact-result__action"
              onClick={() => setSubmissionResult(null)}
            >
              {t("Close")}
            </button>
          </section>
        </div>
      )}
    </>
  )
}